<section class="kyc-dashboard" aria-labelledby="dashboard-title">
  <div class="container">
    <header class="page-hero">
      <div class="page-hero__copy">
        <p class="page-kicker">Centro operativo</p>
        <h1 id="dashboard-title" class="section-title">KYC Dashboard</h1>
        <p class="page-description">
          Supervise el rendimiento del proceso KYC, detecte cuellos de botella y reaccione rápido
          ante casos denegados.
        </p>
      </div>

      <div class="page-hero__actions">
        <button class="btn btn-secondary" type="button" (click)="load()" [disabled]="loading()">
          Actualizar panel
        </button>
      </div>
    </header>

    @if (loading()) {
      <p class="panel-state" role="status">Cargando métricas...</p>
    } @else if (metrics(); as currentMetrics) {
      <div class="overview-grid" aria-label="Resumen estratégico">
        <article class="overview-card overview-card--highlight">
          <span class="overview-label">Tasa de aprobación</span>
          <strong>{{ verificationRate() }}%</strong>
          <p>
            {{ currentMetrics.verified }} de {{ currentMetrics.total }} casos han sido aprobados
            correctamente.
          </p>
        </article>

        <article class="overview-card">
          <span class="overview-label">Próxima prioridad</span>
          <strong>{{ currentMetrics.pending }}</strong>
          <p>{{ spotlight()?.pendingFocus }}</p>
        </article>

        <article class="overview-card">
          <span class="overview-label">Riesgo inmediato</span>
          <strong>{{ currentMetrics.failed }}</strong>
          <p>{{ spotlight()?.failureFocus }}</p>
        </article>
      </div>

      <div class="metrics-region" role="region" aria-labelledby="metrics-heading">
        <h2 id="metrics-heading" class="sr-only">Métricas por estado</h2>
        <div class="metrics-grid">
          @for (card of metricsCards(); track card.label) {
            <article
              class="metric-card"
              [class.metric-card--positive]="card.tone === 'positive'"
              [class.metric-card--warning]="card.tone === 'warning'"
              [class.metric-card--danger]="card.tone === 'danger'"
            >
              <div class="card-title">{{ card.label }}</div>
              <div class="card-value">{{ card.value }}</div>
              <p class="card-caption">{{ card.caption }}</p>
            </article>
          }
        </div>
      </div>

      <section class="status-breakdown" aria-labelledby="status-breakdown-title">
        <div class="card-header">
          <div>
            <p class="panel-kicker">Distribución</p>
            <h2 id="status-breakdown-title" class="section-subtitle">Estado por etapa</h2>
          </div>
        </div>
        <div class="status-grid">
          @for (card of statusCards(); track card.key) {
            <article
              class="status-card"
              [class.status-card--positive]="card.tone === 'positive'"
              [class.status-card--warning]="card.tone === 'warning'"
              [class.status-card--danger]="card.tone === 'danger'"
            >
              <div class="status-card__value">{{ card.value }}</div>
              <div class="status-card__label">{{ card.label }}</div>
            </article>
          }
        </div>
      </section>

      <div class="dashboard-grid">
        <section class="list-panel" aria-labelledby="history-title">
          <div class="card-header">
            <div>
              <p class="panel-kicker">Actividad</p>
              <h2 id="history-title" class="section-subtitle">Historial reciente</h2>
            </div>
            <span class="panel-chip">{{ history().length }} eventos</span>
          </div>

          <div class="table-wrap">
            <table class="history">
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Cliente</th>
                  <th scope="col">Estado</th>
                  <th scope="col">Fecha</th>
                </tr>
              </thead>
              <tbody>
                @for (entry of history(); track entry.id) {
                  <tr>
                    <td>{{ entry.id }}</td>
                    <td>{{ entry.customer }}</td>
                    <td>
                      <span
                        class="status-pill"
                        [class.status-pill--positive]="statusTone(entry.status) === 'positive'"
                        [class.status-pill--warning]="statusTone(entry.status) === 'warning'"
                        [class.status-pill--danger]="statusTone(entry.status) === 'danger'"
                      >
                        {{ statusLabel(entry.status) }}
                      </span>
                    </td>
                    <td>{{ entry.at | date: 'medium' }}</td>
                  </tr>
                }

                @if (history().length === 0) {
                  <tr>
                    <td colspan="4">No hay registros recientes.</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </section>

        <aside class="editor-panel" aria-labelledby="insights-title">
          <div class="card-header">
            <div>
              <p class="panel-kicker">Observación</p>
              <h2 id="insights-title" class="section-subtitle">Señales rápidas</h2>
            </div>
          </div>

          <div class="insight-stack">
            <article class="insight-item">
              <span class="overview-label">Último caso</span>
              @if (latestEntry(); as latestEntryItem) {
                <strong>{{ latestEntryItem.customer }}</strong>
                <p>
                  {{ statusLabel(latestEntryItem.status) }} ·
                  {{ latestEntryItem.at | date: 'medium' }}
                </p>
              } @else {
                <strong>Sin actividad reciente</strong>
                <p>El panel mostrará aquí el evento más reciente cuando exista actividad.</p>
              }
            </article>

            <article class="insight-item">
              <span class="overview-label">Pendientes</span>
              <strong>{{ currentMetrics.pending }}</strong>
              <p>
                Use esta cifra para priorizar revisiones manuales y evitar acumulación operativa.
              </p>
            </article>

            <article class="insight-item">
              <span class="overview-label">Resultado actual</span>
              <strong>{{ verificationRate() }}% aprobado</strong>
              <p>
                Mantenga el ritmo de aprobación sin perder visibilidad sobre los casos denegados.
              </p>
            </article>
          </div>
        </aside>
      </div>
    } @else {
      <p class="panel-state panel-state--error" role="alert">
        No se pudieron cargar las métricas del panel.
      </p>
    }
  </div>
</section>
